// 'use client';

import { Text } from '@lmn/design-system';
import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { NAVIGATION_STEPS } from '@/app/common/navigation.ts';
import { IconChevronRight } from '@/app/common/icons/libraryIcons';
import { PAGE_TYPE } from '@/app/common/pageType.ts';
import { searchPageStyles } from '@/app/components/searchPageStyled.ts';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import LoadLine from '@/app/components/Loadline.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { SearchModes } from '@/domainLogic/index.ts';

import { Icon } from '@/app/common/icons/Icon';
import customCss from './hotel-detail/css/customCss';

const NavigationContainer = styled.div<{
  isTablet?: boolean;
  $currentPage: PAGE_TYPE;
  $evolutionResultsOnlyEnabled?: boolean;
}>`
  display: flex;
  justify-content: center;
  gap: 4px;
  overflow: hidden;

  @media ${customCss.breakpoints.sm} {
    padding: 24px 24px 0;
  }
  @media ${customCss.breakpoints.lg} {
    padding: 24px 24px 0;
  }
  @media ${customCss.breakpoints.lgPlus} {
    padding: 24px 40px 0;
  }
  @media ${customCss.breakpoints.xl} {
    padding: 24px 0 0;
    margin: ${({ $currentPage }) => ($currentPage === PAGE_TYPE.TRANSPORT_DETAIL ? '0 auto' : '0 208px')};
    max-width: 1200px;
  }
  @media (min-width: 1615px) {
    margin: 0 auto;
  }

  ${searchPageStyles};
`;

const NavigationSteps = styled.div`
  display: flex;
  flex-grow: 1;
  padding: 16px 0;
  overflow: auto;
  background-color: white;
  box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};
  position: relative;

  @media ${customCss.breakpoints.sm} {
    border-radius: 8px;
    box-shadow: none;
  }

  @media ${customCss.breakpoints.xl} {
    border-radius: 8px;
    box-shadow: none;
  }
`;

const NavigationScroll = styled.nav`
  display: flex;
  padding: 0 16px;
  gap: 8px;
  align-items: center;
  flex-wrap: nowrap;
  overflow: auto;
`;

const Step = styled(Text)<{ $selected: boolean; $isLink: boolean }>`
  white-space: nowrap;
  ${({ $selected, $isLink }) => {
    if ($selected) {
      return css`
        font-weight: bold;
      `;
    } else {
      if ($isLink) {
        return css`
          color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
          cursor: pointer;
        `;
      }

      return css``;
    }
  }}
`;

const ChevronRight = styled(IconChevronRight)`
  flex-shrink: 0;
`;

const BackButton = styled.button`
  background-color: white;
  border-radius: 8px;
  padding: 16px;
  display: none;
  cursor: pointer;

  @media ${customCss.breakpoints.sm} {
    display: flex;
  }
`;

type NavigationProps = {
  currentPage: PAGE_TYPE;
  handleGoToNextStep: (pageTypeNextPage: PAGE_TYPE) => void;
};

const hotelDetailStep: number = NAVIGATION_STEPS.indexOf(PAGE_TYPE.HOTEL_DETAIL);
const searchStep: number = NAVIGATION_STEPS.indexOf(PAGE_TYPE.SEARCH);
const flightDetailStep: number = NAVIGATION_STEPS.indexOf(PAGE_TYPE.TRANSPORT_DETAIL);
const reviewStep: number = NAVIGATION_STEPS.indexOf(PAGE_TYPE.REVIEW);

export default function Navigation({ currentPage, handleGoToNextStep }: NavigationProps) {
  const currentNavigationStep: number = NAVIGATION_STEPS.indexOf(currentPage);

  const {
    searchInfoData,
    commonData: { isApp },
  } = usePageWrapperData();
  const { t } = useTranslation();
  const { isMobile, isTablet, isEvoLayout } = useCommonClient();
  const { searchMode } = searchInfoData;
  const scrollRef = useRef<HTMLDivElement>(null);
  const isTrain = searchMode === SearchModes.TH;

  const goToPage = (pageTypeNextPage: PAGE_TYPE) => () => {
    const targetNavigationStep = NAVIGATION_STEPS.indexOf(pageTypeNextPage);
    if (targetNavigationStep >= currentNavigationStep) {
      return;
    }
    handleGoToNextStep(pageTypeNextPage);
  };

  const goToPreviousStep = () => {
    handleGoToNextStep(currentNavigationStep === 0 ? PAGE_TYPE.SEARCH : NAVIGATION_STEPS[currentNavigationStep - 1]);
  };

  useEffect(() => {
    const scrollDiv = scrollRef.current;
    if (!scrollDiv) return;

    switch (currentPage) {
      case PAGE_TYPE.SEARCH:
        scrollDiv.scrollTo({ left: 0, behavior: 'smooth' });
        break;
      case PAGE_TYPE.HOTEL_DETAIL:
        scrollDiv.scrollTo({ left: 0, behavior: 'smooth' });
        break;
      case PAGE_TYPE.TRANSPORT_DETAIL:
        scrollDiv.scrollTo({ left: (scrollDiv.scrollWidth - scrollDiv.clientWidth) / 2, behavior: 'smooth' });
        break;
      case PAGE_TYPE.REVIEW:
        scrollDiv.scrollTo({ left: scrollDiv.scrollWidth, behavior: 'smooth' });
        break;
      default:
        break;
    }
  }, [currentPage]);

  return (
    <NavigationContainer isTablet={isTablet} $currentPage={currentPage} $evolutionResultsOnlyEnabled={isEvoLayout}>
      {currentNavigationStep !== searchStep && !isApp && (
        <BackButton onClick={goToPreviousStep}>
          <Icon category="Arrows" name="icon-arrow-left" size={24} color="iconPrimary" alt="Back to search page" />
        </BackButton>
      )}
      <NavigationSteps>
        <NavigationScroll ref={scrollRef}>
          <Step
            variant="BodySmall"
            id={PAGE_TYPE.HOTEL_DETAIL}
            onClick={goToPage(PAGE_TYPE.HOTEL_DETAIL)}
            $selected={currentNavigationStep === hotelDetailStep || currentNavigationStep === searchStep}
            $isLink={currentNavigationStep > hotelDetailStep}
          >
            {t('vacation.oss.navigation.search')}
          </Step>

          <ChevronRight size={16} />
          <Step
            variant="BodySmall"
            id={PAGE_TYPE.TRANSPORT_DETAIL}
            onClick={goToPage(PAGE_TYPE.TRANSPORT_DETAIL)}
            $selected={currentNavigationStep === flightDetailStep}
            $isLink={currentNavigationStep > flightDetailStep}
          >
            {t(isTrain ? 'vacation.oss.navigation.train' : 'vacation.oss.navigation.flight')}
          </Step>
          <ChevronRight size={16} />
          <Step
            variant="BodySmall"
            id={PAGE_TYPE.REVIEW}
            $selected={currentNavigationStep === reviewStep}
            $isLink={false}
          >
            {t(
              isMobile
                ? isTrain
                  ? 'vacation.oss.navigation.summary.train'
                  : 'vacation.oss.navigation.summary'
                : isTrain
                  ? 'vacation.oss.navigation.personalise.train'
                  : 'vacation.oss.navigation.personalise'
            )}
          </Step>
        </NavigationScroll>
        <LoadLine $isGlobal={true} />
      </NavigationSteps>
    </NavigationContainer>
  );
}
